import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { MetasService, ContasService, fmtMoeda, fmtData } from '../services';

const initForm = { nome: '', descricao: '', valor_objetivo: '', valor_atual: '', data_prazo: '', conta_id: '', cor: '#16a34a', icone: '🎯' };
const initAporte = { valor: '', data: new Date().toISOString().split('T')[0], observacoes: '' };

export default function Metas() {
  const [metas, setMetas] = useState([]);
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [modalAporte, setModalAporte] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initForm);
  const [aporte, setAporte] = useState(initAporte);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [tab, setTab] = useState('ativa');

  const load = () => {
    setLoading(true);
    Promise.all([MetasService.listar(), ContasService.listar()])
      .then(([m, c]) => { setMetas(m); setContas(c); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const metasFiltradas = metas.filter(m => m.status === tab);

  const openNew = () => { setEditing(null); setForm(initForm); setErro(''); setModal(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...m, conta_id: m.conta_id || '' }); setErro(''); setModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro('');
    try {
      if (editing) await MetasService.atualizar(editing.id, form);
      else await MetasService.criar(form);
      setModal(false); load();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleAporte = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await MetasService.aporte(modalAporte.id, aporte);
      setModalAporte(null); setAporte(initAporte); load();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao aportar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta meta?')) return;
    try { await MetasService.excluir(id); load(); }
    catch (err) { alert(err.response?.data?.erro || 'Erro'); }
  };

  return (
    <Layout title="Metas Financeiras">
      <div className="flex-between mb-3">
        <div className="tabs" style={{ borderBottom: 'none', marginBottom: 0 }}>
          {[['ativa', '🎯 Ativas'], ['concluida', '✅ Concluídas'], ['cancelada', '❌ Canceladas']].map(([k, v]) => (
            <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{v}</button>
          ))}
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Nova Meta</button>
      </div>

      {loading ? (
        <div className="loading-wrap"><div className="spinner" /></div>
      ) : metasFiltradas.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-icon">🎯</div>
          <p>Nenhuma meta {tab === 'ativa' ? 'ativa' : tab === 'concluida' ? 'concluída' : 'cancelada'}.</p>
          {tab === 'ativa' && <button className="btn btn-primary mt-2" onClick={openNew}>Criar meta</button>}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {metasFiltradas.map(m => {
            const pct = Math.min(100, (parseFloat(m.valor_atual) / parseFloat(m.valor_objetivo)) * 100);
            const falta = parseFloat(m.valor_objetivo) - parseFloat(m.valor_atual);
            const diasRestantes = m.data_prazo
              ? Math.ceil((new Date(m.data_prazo) - new Date()) / (1000 * 60 * 60 * 24))
              : null;
            return (
              <div key={m.id} className="card">
                <div className="card-body">
                  <div className="flex-between mb-2">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: m.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                        {m.icone}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{m.nome}</div>
                        {m.descricao && <div className="text-sm text-muted">{m.descricao}</div>}
                      </div>
                    </div>
                    {m.status === 'ativa' && (
                      <div className="flex-gap gap-sm">
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(m)}>✏️</button>
                        <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(m.id)}>🗑️</button>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <div className="flex-between mb-1">
                      <span style={{ fontSize: 20, fontWeight: 700 }}>{fmtMoeda(m.valor_atual)}</span>
                      <span style={{ fontSize: 13, color: 'var(--gray-400)' }}>de {fmtMoeda(m.valor_objetivo)}</span>
                    </div>
                    <div className="progress-bar-wrap">
                      <div className="progress-bar-fill" style={{ width: `${pct}%`, background: m.cor }} />
                    </div>
                    <div className="flex-between mt-1">
                      <span className="text-sm" style={{ color: m.cor, fontWeight: 600 }}>{pct.toFixed(1)}%</span>
                      {falta > 0 && <span className="text-sm text-muted">Falta {fmtMoeda(falta)}</span>}
                    </div>
                  </div>

                  {m.data_prazo && (
                    <div style={{ fontSize: 12, color: diasRestantes !== null && diasRestantes < 30 ? 'var(--warning)' : 'var(--gray-400)', marginBottom: 8 }}>
                      📅 Prazo: {fmtData(m.data_prazo)}
                      {diasRestantes !== null && diasRestantes > 0 && ` (${diasRestantes} dias)`}
                      {diasRestantes !== null && diasRestantes <= 0 && ' (vencida)'}
                    </div>
                  )}

                  {m.status === 'ativa' && (
                    <button className="btn btn-success btn-sm" style={{ width: '100%', justifyContent: 'center' }}
                      onClick={() => { setModalAporte(m); setAporte(initAporte); }}>
                      + Adicionar Aporte
                    </button>
                  )}
                  {m.status === 'concluida' && (
                    <div style={{ textAlign: 'center', padding: '6px', background: 'var(--success-light)', borderRadius: 6, fontSize: 12, color: 'var(--success)', fontWeight: 600 }}>
                      ✅ Meta concluída!
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal nova/editar meta */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-md">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Editar Meta' : 'Nova Meta'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {erro && <div className="alert alert-danger">{erro}</div>}
                <div className="form-group">
                  <label className="form-label">Nome da meta *</label>
                  <input className="form-control" required value={form.nome}
                    onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Descrição</label>
                  <input className="form-control" value={form.descricao || ''}
                    onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Valor objetivo (R$) *</label>
                    <input type="number" step="0.01" min="0.01" className="form-control" required value={form.valor_objetivo}
                      onChange={e => setForm(p => ({ ...p, valor_objetivo: e.target.value }))} />
                  </div>
                  {!editing && (
                    <div className="form-group">
                      <label className="form-label">Valor atual (R$)</label>
                      <input type="number" step="0.01" className="form-control" value={form.valor_atual}
                        onChange={e => setForm(p => ({ ...p, valor_atual: e.target.value }))} />
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Prazo</label>
                    <input type="date" className="form-control" value={form.data_prazo || ''}
                      onChange={e => setForm(p => ({ ...p, data_prazo: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Conta associada</label>
                    <select className="form-control" value={form.conta_id}
                      onChange={e => setForm(p => ({ ...p, conta_id: e.target.value }))}>
                      <option value="">Nenhuma</option>
                      {contas.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ícone</label>
                    <input className="form-control" value={form.icone} maxLength={4}
                      onChange={e => setForm(p => ({ ...p, icone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cor</label>
                    <input type="color" className="form-control" value={form.cor} style={{ height: 40, padding: 4 }}
                      onChange={e => setForm(p => ({ ...p, cor: e.target.value }))} />
                  </div>
                  {editing && (
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select className="form-control" value={form.status}
                        onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                        <option value="ativa">Ativa</option>
                        <option value="concluida">Concluída</option>
                        <option value="cancelada">Cancelada</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal aporte */}
      {modalAporte && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModalAporte(null)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">Aporte — {modalAporte.nome}</span>
              <button className="modal-close" onClick={() => setModalAporte(null)}>×</button>
            </div>
            <form onSubmit={handleAporte}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Valor do aporte (R$) *</label>
                  <input type="number" step="0.01" min="0.01" className="form-control" required value={aporte.valor}
                    onChange={e => setAporte(p => ({ ...p, valor: e.target.value }))} autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Data *</label>
                  <input type="date" className="form-control" required value={aporte.data}
                    onChange={e => setAporte(p => ({ ...p, data: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Observações</label>
                  <input className="form-control" value={aporte.observacoes}
                    onChange={e => setAporte(p => ({ ...p, observacoes: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setModalAporte(null)}>Cancelar</button>
                <button type="submit" className="btn btn-success" disabled={saving}>{saving ? 'Salvando...' : 'Aportar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
